import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, IconButton, LinearProgress, Menu, MenuItem, Paper, Skeleton, Stack, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ShareIcon from '@mui/icons-material/IosShare';
import JSZip from 'jszip';
import { useNavigate, useParams } from 'react-router-dom';
import eventApi from '../EventGallery/eventApi';
import { useAuth } from '../../contexts/AuthContext';
import { Capacitor } from '@capacitor/core';
import { pollPresignedUrl, saveBlob } from '../../utils/ucOriginalDownload';

const BUFFER_DB_NAME = 'cm-share-download-buffer';
const BUFFER_STORE_NAME = 'photos';

// Presigned URLs for Drive-backed uncompressed originals (from
// getShareUcOriginals) may point at an S3 cache object the backend is still
// relaying from the organiser's Drive in the background - 404 until it
// lands, 200 once cached (S3-backed ones resolve on the very first try, no
// wait). pollPresignedUrl polls the same presigned URL directly against S3 -
// no per-file backend round trip needed, see share_uc_originals on the EC2
// backend - and is the same "presign + poll" primitive every "Download
// uncompressed original" affordance in the app uses (3s interval, ~5min
// per-file timeout; see utils/ucOriginalDownload).
const fetchUcOriginal = pollPresignedUrl;

function openBufferDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(BUFFER_DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(BUFFER_STORE_NAME)) {
        req.result.createObjectStore(BUFFER_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbPut(db, key, value) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BUFFER_STORE_NAME, 'readwrite');
    tx.objectStore(BUFFER_STORE_NAME).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbGet(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BUFFER_STORE_NAME, 'readonly');
    const req = tx.objectStore(BUFFER_STORE_NAME).get(key);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbDelete(db, key) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BUFFER_STORE_NAME, 'readwrite');
    tx.objectStore(BUFFER_STORE_NAME).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function idbClear(db) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(BUFFER_STORE_NAME, 'readwrite');
    tx.objectStore(BUFFER_STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export default function SharePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [preview, setPreview] = useState(null); // { photoId, index }
  const [downloading, setDownloading] = useState(''); // photo_id currently downloading as a compressed original
  const [downloadingUc, setDownloadingUc] = useState(''); // photo_id currently downloading as an uncompressed original
  const [zipProgress, setZipProgress] = useState(null); // { kind: 'original'|'uncompressed', phase: 'preparing'|'downloading'|'compressing', current, total }
  const [sharingPhoto, setSharingPhoto] = useState('');
  const [originals, setOriginals] = useState({});
  const [ucOriginals, setUcOriginals] = useState({});
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const previewRef = useRef(null);
  const originalsPromiseRef = useRef(null);
  const ucOriginalsPromiseRef = useRef(null);

  // Original-resolution URLs are expensive to presign (a metadata.json lookup
  // per photo), so they're fetched from the server on demand — the first time
  // a download/share is actually requested — rather than at page load.
  const ensureOriginals = useCallback(() => {
    if (!originalsPromiseRef.current) {
      originalsPromiseRef.current = eventApi.getShareOriginals(token)
        .then(d => {
          const map = d?.originals || {};
          setOriginals(map);
          return map;
        })
        .catch(err => {
          originalsPromiseRef.current = null;
          throw err;
        });
    }
    return originalsPromiseRef.current;
  }, [token]);

  // Only ever contains photo IDs that have a genuinely distinct uncompressed
  // original (see face_group/main.py / event_photo_uc_original) — fetched
  // lazily, same as ensureOriginals, the first time it's actually needed.
  // Each URL is a presigned S3 GET — for Drive-backed photos, the backend
  // hands these back *before* relaying finishes and starts populating them
  // in the background, so the URL may still 404 for a bit; fetchUcOriginal
  // polls it directly until the object lands. See share_uc_originals.
  const ensureUcOriginals = useCallback(() => {
    if (!ucOriginalsPromiseRef.current) {
      ucOriginalsPromiseRef.current = eventApi.getShareUcOriginals(token)
        .then(d => {
          const map = d?.uc_originals || {};
          setUcOriginals(map);
          return map;
        })
        .catch(err => {
          ucOriginalsPromiseRef.current = null;
          throw err;
        });
    }
    return ucOriginalsPromiseRef.current;
  }, [token]);

  useEffect(() => {
    if (!token) { setError('No share token provided.'); setLoading(false); return; }
    eventApi.getShareData(token)
      .then(d => setData(d))
      .catch(err => setError(err?.response?.data?.error || err.message || 'This share link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Newest-first by capture date (data.captured_at, keyed by photo_id) -
  // photos without a known capture date sink to the end, keeping their
  // existing relative order.
  const photoIds = useMemo(() => {
    const ids = data?.photo_ids || [];
    const capturedAt = data?.captured_at || {};
    const dated = [];
    const undated = [];
    for (const pid of ids) (capturedAt[pid] ? dated : undated).push(pid);
    dated.sort((a, b) => (capturedAt[a] < capturedAt[b] ? 1 : capturedAt[a] > capturedAt[b] ? -1 : 0));
    return [...dated, ...undated];
  }, [data]);
  const thumbnails = data?.thumbnails || {};

  function openPreview(photoId) {
    const index = photoIds.indexOf(photoId);
    setPreview({ photoId, index: index >= 0 ? index : 0 });
    setDownloadMenuAnchor(null);
    // Lazily fetch original-download links the first time the visitor opens a preview
    ensureOriginals().catch(() => {});
    ensureUcOriginals().catch(() => {});
  }

  function closePreview() { setPreview(null); setDownloadMenuAnchor(null); }

  const navigatePreview = useCallback((dir) => {
    if (!preview) return;
    const next = preview.index + dir;
    if (next < 0 || next >= photoIds.length) return;
    setPreview({ photoId: photoIds[next], index: next });
    setDownloadMenuAnchor(null);
  }, [preview, photoIds]);

  useEffect(() => {
    if (!preview) return;
    const handler = (e) => {
      if (e.key === 'ArrowLeft') navigatePreview(-1);
      if (e.key === 'ArrowRight') navigatePreview(1);
      if (e.key === 'Escape') closePreview();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [preview, navigatePreview]);

  // kind: 'original' (compressed copy CircleMoments stores) or 'uncompressed'
  // (a genuinely distinct, higher-quality original — only ever resolved when
  // one actually exists, see ensureUcOriginals).
  async function downloadPhotoByKind(photoId, kind) {
    const isUc = kind === 'uncompressed';
    const setBusy = isUc ? setDownloadingUc : setDownloading;
    setBusy(photoId);
    try {
      const map = await (isUc ? ensureUcOriginals() : ensureOriginals());
      const url = map[photoId];
      if (!url) return;
      try {
        const res = await (isUc ? fetchUcOriginal(url) : fetch(url));
        const blob = await res.blob();
        await saveBlob(blob, `photo-${photoId}${isUc ? '-uncompressed' : ''}.jpg`);
      } catch (err) {
        // window.open on a bare Capacitor WebView is a silent no-op (no
        // onCreateWindow handler registered anywhere in the native app) - it
        // can only work as a fallback in a real browser tab.
        if (Capacitor.isNativePlatform()) throw err;
        window.open(url, '_blank', 'noopener');
      }
    } catch {
      window.alert('Could not load the download link. Please try again.');
    } finally {
      setBusy('');
    }
  }

  function downloadPhoto(photoId) { return downloadPhotoByKind(photoId, 'original'); }

  // Buffers each photo to IndexedDB (disk-backed, never holding more than one
  // photo's bytes in memory at a time) before streaming the blobs into a zip —
  // shared between the "Download all" and "Download all uncompressed" flows;
  // for uncompressed, only photo IDs that actually resolved to a genuinely
  // distinct original are included (out of 30 shared photos, if only 10 have
  // one, the zip contains exactly those 10 — never a duplicate of the
  // compressed copy).
  async function downloadAllByKind(kind) {
    if (!photoIds.length || zipProgress) return;
    const isUc = kind === 'uncompressed';
    let db;
    try {
      setZipProgress({ kind, phase: 'preparing', current: 0, total: 0 });
      const urlMap = await (isUc ? ensureUcOriginals() : ensureOriginals());
      const ids = isUc ? photoIds.filter(pid => urlMap[pid]) : photoIds;
      if (!ids.length) {
        setZipProgress(null);
        if (isUc) window.alert('None of these photos have a separate uncompressed original to download.');
        return;
      }
      const total = ids.length;
      db = await openBufferDb();

      // Phase 1 — fetch each original and buffer it in IndexedDB (disk-backed),
      // never holding more than one photo's bytes in JS memory at a time.
      setZipProgress({ kind, phase: 'downloading', current: 0, total });
      for (let i = 0; i < total; i++) {
        const pid = ids[i];
        const url = urlMap[pid];
        if (url) {
          const res = await (isUc ? fetchUcOriginal(url) : fetch(url));
          const blob = await res.blob();
          await idbPut(db, pid, blob);
        }
        setZipProgress({ kind, phase: 'downloading', current: i + 1, total });
      }

      // Phase 2 — stream the buffered blobs out of IndexedDB straight into the zip,
      // freeing each one from local storage as soon as it's been queued for compression.
      const zip = new JSZip();
      const suffix = isUc ? '-uncompressed' : '';
      for (let i = 0; i < total; i++) {
        const pid = ids[i];
        const blob = await idbGet(db, pid);
        if (blob) zip.file(`photo-${pid}${suffix}.jpg`, blob);
        await idbDelete(db, pid);
      }

      setZipProgress({ kind, phase: 'compressing', current: 0, total: 100 });
      const zipBlob = await zip.generateAsync(
        { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 }, streamFiles: true },
        meta => setZipProgress({ kind, phase: 'compressing', current: Math.round(meta.percent), total: 100 })
      );

      const safeName = (data?.event_name || 'shared-photos').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60) || 'shared-photos';
      await saveBlob(zipBlob, `${safeName}${suffix}.zip`);
    } catch (err) {
      window.alert('Could not prepare the zip download. Please try again.');
    } finally {
      if (db) {
        try { await idbClear(db); } catch { /* ignore cleanup errors */ }
        db.close();
      }
      setZipProgress(null);
    }
  }

  async function sharePhoto(photo) {
    if (!photo || sharingPhoto) return;
    setSharingPhoto(photo.id);
    try {
      let originalUrl = photo.original;
      try {
        const map = await ensureOriginals();
        originalUrl = map[photo.id] || originalUrl;
      } catch { /* fall back to whatever URL we already have, if any */ }

      if (navigator.share) {
        if (navigator.canShare && originalUrl) {
          try {
            const res = await fetch(originalUrl);
            const blob = await res.blob();
            const file = new File([blob], `photo-${photo.id}.jpg`, { type: blob.type || 'image/jpeg' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({ files: [file], title: data?.event_name || 'CircleMoments photo' });
              return;
            }
          } catch { /* fall through to link share */ }
        }
        await navigator.share({ title: data?.event_name || 'CircleMoments photo', url: originalUrl || window.location.href });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(originalUrl || window.location.href);
        window.alert('Link copied to clipboard');
      }
    } catch { /* user cancelled the share sheet — ignore */ }
    finally {
      setSharingPhoto('');
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="md">
          <Skeleton variant="text" width={300} height={40} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 3 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1 }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={160} sx={{ borderRadius: 1.5 }} />
            ))}
          </Box>
        </Container>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={700} mb={2}>Share link unavailable</Typography>
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
          <Button variant="contained" onClick={() => navigate('/')}>Go to CircleMoments</Button>
        </Container>
      </Box>
    );
  }

  const previewPhoto = preview ? { id: preview.photoId, thumb: thumbnails[preview.photoId], original: originals[preview.photoId] } : null;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ background: 'linear-gradient(135deg, #fff4f8 0%, #fff8ef 100%)', borderBottom: '1px solid', borderColor: 'divider', py: 2.5, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="md">
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>CircleMoments</Typography>
              </Stack>
              <Typography variant="h5" fontWeight={800} color="#1a237e">
                {data?.event_name || 'Shared Photos'}
              </Typography>
              {data?.recipient_name && (
                <Typography variant="body2" color="text.secondary">
                  Photos shared with <strong>{data.recipient_name}</strong>
                  {data.event_date && ` · ${new Date(data.event_date).toLocaleDateString()}`}
                </Typography>
              )}
            </Box>
            {(user || data?.upload_link_token) && (
              <Stack direction="row" spacing={1} flexShrink={0} flexWrap="wrap">
                {user && (data?.is_member ? (
                  <Tooltip title="View photos shared to your account">
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => navigate(`/event/${data.event_id}`)}
                      sx={{ textTransform: 'none' }}
                    >
                      View Event
                    </Button>
                  </Tooltip>
                ) : data?.invite_code && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate(`/join-event/${data.invite_code}`)}
                    sx={{ textTransform: 'none' }}
                  >
                    Join This Event
                  </Button>
                ))}
                {data?.upload_link_token && (
                  <Tooltip title="Submit your own photos from this event">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate(`/upload/${data.upload_link_token}`)}
                      sx={{ textTransform: 'none' }}
                    >
                      Submit Photos
                    </Button>
                  </Tooltip>
                )}
                {user && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/dashboard')}
                    sx={{ textTransform: 'none' }}
                  >
                    My Dashboard
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Container>
      </Box>

      {/* Gallery */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {photoIds.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography color="text.secondary">No photos have been shared yet. Check back soon!</Typography>
          </Box>
        ) : (
          <>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" spacing={1} mb={2}>
              <Typography variant="body2" color="text.secondary">
                {photoIds.length} photo{photoIds.length !== 1 ? 's' : ''} — click any photo to preview and download
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={zipProgress?.kind === 'original' ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                  onClick={() => downloadAllByKind('original')}
                  disabled={!!zipProgress}
                  sx={{ textTransform: 'none' }}
                >
                  {zipProgress?.kind === 'original' ? 'Preparing…' : 'Download all'}
                </Button>
                <Tooltip title="Only includes photos that have a separate, higher-quality original">
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={zipProgress?.kind === 'uncompressed' ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                      onClick={() => downloadAllByKind('uncompressed')}
                      disabled={!!zipProgress}
                      sx={{ textTransform: 'none' }}
                    >
                      {zipProgress?.kind === 'uncompressed' ? 'Preparing…' : 'Download all uncompressed'}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 1 }}>
              {photoIds.map(pid => (
                <Box
                  key={pid}
                  onClick={() => openPreview(pid)}
                  sx={{
                    position: 'relative', cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden',
                    aspectRatio: '1', bgcolor: 'grey.100',
                    '&:hover .photo-overlay': { opacity: 1 },
                    '&:hover img': { transform: 'scale(1.04)' },
                  }}
                >
                  <Box
                    component="img"
                    src={thumbnails[pid]}
                    alt="Event photo"
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.2s ease', display: 'block' }}
                  />
                  <Box
                    className="photo-overlay"
                    sx={{
                      position: 'absolute', inset: 0, opacity: 0,
                      bgcolor: 'rgba(0,0,0,0.35)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'opacity 0.15s',
                    }}
                  >
                    <DownloadIcon sx={{ color: '#fff', fontSize: 28 }} />
                  </Box>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Container>

      {/* Footer */}
      <Box sx={{ py: 3, textAlign: 'center', borderTop: '1px solid', borderColor: 'divider', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Powered by{' '}
          <Box component="a" href="https://www.circlemoments.com" target="_blank" rel="noopener noreferrer"
            sx={{ color: '#C73866', textDecoration: 'none', fontWeight: 700 }}>
            CircleMoments
          </Box>
        </Typography>
      </Box>

      {/* Floating zip-download progress */}
      {zipProgress && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed', top: 16, right: 16, zIndex: 1300,
            width: 260, p: 1.5, borderRadius: 2,
            border: '1px solid', borderColor: 'divider',
          }}
        >
          <Typography variant="caption" fontWeight={700} color="#C73866" sx={{ display: 'block', mb: 0.5 }}>
            {zipProgress.kind === 'uncompressed' ? 'Uncompressed originals — ' : ''}
            {zipProgress.phase === 'preparing' && 'Preparing download links…'}
            {zipProgress.phase === 'downloading' && 'Downloading photos…'}
            {zipProgress.phase === 'compressing' && 'Compressing into zip…'}
          </Typography>
          <LinearProgress
            variant={zipProgress.phase === 'preparing' ? 'indeterminate' : 'determinate'}
            value={zipProgress.total ? (zipProgress.current / zipProgress.total) * 100 : 0}
            sx={{ borderRadius: 1, height: 6, mb: 0.5, '& .MuiLinearProgress-bar': { bgcolor: '#C73866' } }}
          />
          <Typography variant="caption" color="text.secondary">
            {zipProgress.phase === 'preparing' && 'Fetching secure original-photo links…'}
            {zipProgress.phase === 'downloading' && `${zipProgress.current} / ${zipProgress.total} photos`}
            {zipProgress.phase === 'compressing' && `${zipProgress.current}%`}
          </Typography>
        </Paper>
      )}

      {/* Preview dialog */}
      <Dialog
        open={Boolean(preview)}
        onClose={closePreview}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#111', borderRadius: 2, m: { xs: 1, sm: 2 } } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }} ref={previewRef}>
          {/* Share */}
          {previewPhoto && (navigator.share || navigator.clipboard) && (
            <Tooltip title="Share photo">
              <span>
                <IconButton
                  size={isMobile ? 'small' : 'medium'}
                  onClick={() => sharePhoto(previewPhoto)}
                  disabled={sharingPhoto === previewPhoto.id}
                  sx={{ position: 'absolute', top: 8, right: { xs: 48, sm: 56 }, zIndex: 10, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}
                >
                  {sharingPhoto === previewPhoto.id ? <CircularProgress size={18} color="inherit" /> : <ShareIcon fontSize={isMobile ? 'small' : 'medium'} />}
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Close */}
          <IconButton
            size={isMobile ? 'small' : 'medium'}
            onClick={closePreview}
            sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}
          >
            <CloseIcon fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>

          {/* Prev */}
          {preview && preview.index > 0 && (
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              onClick={() => navigatePreview(-1)}
              sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 10, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}
            >
              <ArrowBackIosNewIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          )}

          {previewPhoto && (
            <Box
              component="img"
              src={previewPhoto.original || previewPhoto.thumb}
              alt="Preview"
              sx={{ maxWidth: '100%', maxHeight: { xs: '70vh', sm: '80vh' }, objectFit: 'contain', display: 'block' }}
            />
          )}

          {/* Next */}
          {preview && preview.index < photoIds.length - 1 && (
            <IconButton
              size={isMobile ? 'small' : 'medium'}
              onClick={() => navigatePreview(1)}
              sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 10, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)' }}
            >
              <ArrowForwardIosIcon fontSize={isMobile ? 'small' : 'medium'} />
            </IconButton>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: '#111',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="caption" color="grey.400" sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
            {preview ? `${preview.index + 1} / ${photoIds.length}` : ''}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ justifyContent: { xs: 'center', sm: 'flex-end' } }}
          >
            <Tooltip title="Download original">
              <span>
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  startIcon={downloading === previewPhoto?.id ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                  onClick={() => previewPhoto && downloadPhoto(previewPhoto.id)}
                  disabled={!previewPhoto?.original || downloading === previewPhoto?.id}
                  sx={{ textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  {downloading === previewPhoto?.id ? 'Downloading…' : (isMobile ? 'Download' : 'Download original')}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="More download options">
              <span>
                <Button
                  variant="contained"
                  size={isMobile ? 'small' : 'medium'}
                  onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
                  disabled={!previewPhoto?.original}
                  sx={{ textTransform: 'none', minWidth: 0, px: 0.5, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: '1px solid rgba(255,255,255,0.3)' }}
                >
                  <ArrowDropDownIcon />
                </Button>
              </span>
            </Tooltip>
            <Menu anchorEl={downloadMenuAnchor} open={!!downloadMenuAnchor} onClose={() => setDownloadMenuAnchor(null)}>
              <MenuItem
                disabled={!previewPhoto || !ucOriginals[previewPhoto.id] || downloadingUc === previewPhoto?.id}
                onClick={() => {
                  setDownloadMenuAnchor(null);
                  if (previewPhoto) downloadPhotoByKind(previewPhoto.id, 'uncompressed');
                }}
              >
                {downloadingUc === previewPhoto?.id ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                Download uncompressed original
              </MenuItem>
            </Menu>
          </Stack>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
